import { Row, Rows } from "../../../interfaces";
import { centerNoBold, kpiHeaders, left } from "src/app/util/excelStyles";
import { formatDate, formatMetricValue, withCellStyles } from "../../../utils";
import {
  MetricCrossCompanyExportExcel,
} from "../../../../kpi/components/kpi-metrics-main/cross-company/kpi-cross-company.component";
import { LocalDate } from "src/app/core/pojo/date-variable";
import {
  MetricCrossCompanyDTO,
  CompanyCrossCompanyDTO,
  MetricDataCrossCompanyDTO,
} from "../../../../kpi/components/kpi-metrics-main/cross-company/service/kpi-cross-company.service";

/**
 * Creates the header row with period columns
 */
function createHeaderRow(periods: LocalDate[]): Row {
  const headerRow: Row = [
    withCellStyles('Metric', kpiHeaders),
    withCellStyles('Role', kpiHeaders),
    withCellStyles('Company', kpiHeaders),
    null,
  ];
  periods.forEach((period) => {
    headerRow.push(withCellStyles(formatDate(period), kpiHeaders));
  });
  return headerRow;
}

/**
 * Creates a row for a metric with empty cells for periods
 */
function createMetricRow(metric: MetricCrossCompanyDTO, periodsCount: number): Row {
  const metricRow: Row = [
    withCellStyles(`${metric.groupName}: ${metric.name}`, left),
    withCellStyles(metric.role, centerNoBold),
    withCellStyles('', centerNoBold),
    null,
  ];

  // Add empty cells for periods
  for (let i = 0; i < periodsCount; i++) {
    metricRow.push(withCellStyles('', centerNoBold));
  }

  return metricRow;
}

/**
 * Creates a map of period to data for easier lookup
 */
function createPeriodDataMap(companyData: MetricDataCrossCompanyDTO[]): Map<string, MetricDataCrossCompanyDTO> {
  const periodDataMap = new Map<string, MetricDataCrossCompanyDTO>();
  companyData.forEach((data) => {
    periodDataMap.set(data.period.toString(), data);
  });
  return periodDataMap;
}

/**
 * Creates rows for a company with its data for each period
 */
function createCompanyRows(company: CompanyCrossCompanyDTO, periods: LocalDate[], metric: MetricCrossCompanyDTO): Row | null {
  const companyRow: Row = [
    withCellStyles('', centerNoBold),
    withCellStyles('', centerNoBold),
    withCellStyles(company.companyName, left),
    null,
  ];

  // Create a map of period to data for easier lookup
  const periodDataMap = createPeriodDataMap(company.data);

  // Add data for each period
  periods.forEach((period) => {
    const periodData = periodDataMap.get(period.toString());
    let value = '';

    if (periodData && periodData.rowValue) {
      value = formatMetricValue(periodData.rowValue, metric.dataProps.dataType);
    }

    companyRow.push(withCellStyles(value, centerNoBold));
  });

  return companyRow;
}

export const getPreparedData = (data: MetricCrossCompanyExportExcel): Rows => {
  const newData: Rows = [];

  // Add header row
  newData.push(createHeaderRow(data.periods));

  // Create rows for each metric and its companies
  data.metrics.forEach((metric) => {
    // Add metric row
    newData.push(createMetricRow(metric, data.periods.length));

    // Add company rows
    metric.companies.forEach((company) => {
      if (!company.data.length) {
        return;
      }
      const companyRow = createCompanyRows(company, data.periods, metric);
      if (companyRow) {
        newData.push(companyRow);
      }
    });
  });

  return newData;
};
